import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from './input';
import { useDebounce } from '../../../hooks/useDebounce';

export interface SearchFilterProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /**
   * Valor inicial opcional
   */
  initialValue?: string;
  /**
   * Callback ejecutado al aplicar el debounce de búsqueda
   */
  onSearch: (query: string) => void;
  /**
   * Retraso del debounce en milisegundos. Default: 300ms
   */
  debounceDelay?: number;
}

/**
 * SearchFilter
 * Componente con responsabilidad única de capturar búsquedas de texto.
 * Utiliza un debounce interno para no saturar al momento de escribir.
 * 
 * @example
 * ```tsx
 * const [query, setQuery] = useState('');
 * 
 * return (
 *   <SearchFilter 
 *     placeholder="Buscar por nombre..." 
 *     onSearch={(value) => setQuery(value)} 
 *   />
 * );
 * ```
 */
export function SearchFilter({
  initialValue = '',
  onSearch,
  debounceDelay = 300,
  className = '',
  autoComplete = 'on',
  placeholder = 'Buscar...',
  ...props
}: SearchFilterProps) {
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebounce(value, debounceDelay);

  const onSearchRef = React.useRef(onSearch);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Effect to trigger search when debounced value changes
  useEffect(() => {
    onSearchRef.current(debouncedValue);
  }, [debouncedValue]);

  // Update internal value if external initialValue changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <div className={`relative flex items-center ${className}`}>
      <Search className="absolute left-3 h-4 w-4 shrink-0 text-slate-400" />
      <Input
        name="product-search"
        autoComplete={autoComplete}
        type="search"
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="pl-9 pr-9 h-10 w-full rounded-md border border-slate-200 bg-white text-sm placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
        {...props}
      />
      {value && (
        <button
          type="button"
          onClick={() => setValue('')}
          className="absolute right-3 rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
